import dayjs from 'dayjs';
import {
  type Connector,
  StandingOrderFrequency,
} from 'types/transactions.types';
import type {
  TransferFormFields,
  TransferReviewData,
  TransferSchedule,
} from '../types';
import { createTransferConfirmHandler } from './transferExecution';

/**
 * Transforms form data to TransferReviewData for the review step
 * Standardizes the data transformation process
 */
export const transformFormDataToReviewData = (
  formData: Record<string, any>,
  mutateFunction: (data: any) => void,
  isLoading: boolean,
  sourceAccountId?: string,
  destinationConnector?: Connector,
): TransferReviewData => {
  // Check if it's a regular payment (OneTime period with today's date)
  const firstExecutionDateIsToday = dayjs(formData.firstExecutionDate).isSame(
    dayjs(),
    'day',
  );

  const isRegularPayment =
    formData.period === StandingOrderFrequency.OneTime &&
    firstExecutionDateIsToday;

  // For internal transfers, we need to store the destination account IBAN and name
  // in the originalFormData so it's available for standing orders
  const enrichedFormData = {
    ...formData,
    destinationAccountIBAN: destinationConnector?.bankAccountReference?.IBAN,
    destinationAccountName: destinationConnector?.title,
  };

  const reviewData: TransferReviewData = {
    recipientName: formData.recipient_name,
    recipientIBAN: formData.recipient_iban,
    amount: formData.amount,
    communication: formData.communication,
    instantTransfer: formData.instant,
    schedule: {
      period: formData.period || StandingOrderFrequency.OneTime,
      firstExecutionDate: formData.firstExecutionDate || new Date(),
      lastExecutionDate: formData.lastExecutionDate,
      requestedExecutionAt:
        !isRegularPayment && formData.period === StandingOrderFrequency.OneTime
          ? dayjs(formData.firstExecutionDate).toDate()
          : undefined,
    },
    execution: {
      originalFormData: enrichedFormData,
      mutateFunction,
      isLoading,
    },
  };

  // Create the onConfirm function
  // For internal transfers, the destination account ID is in the originalFormData
  const destinationAccountId = formData.toConnectorId;
  const onConfirm = createTransferConfirmHandler(
    reviewData,
    sourceAccountId,
    destinationAccountId,
  );

  return {
    ...reviewData,
    execution: {
      ...reviewData.execution,
      onConfirm,
    },
  };
};

/**
 * Transforms TransferReviewData to TransferFormFields for form preservation
 * Used when user navigates back to form from review step
 */
export const transformReviewDataToFormFields = (
  reviewData: TransferReviewData,
): TransferFormFields => {
  return {
    recipient_name: reviewData.recipientName,
    recipient_iban: reviewData.recipientIBAN,
    amount: reviewData.amount,
    communication: reviewData.communication,
    requestedExecutionAt: reviewData.schedule.requestedExecutionAt,
    period: reviewData.schedule.period,
    firstExecutionDate: reviewData.schedule.firstExecutionDate,
    lastExecutionDate: reviewData.schedule.lastExecutionDate,
  };
};

/**
 * Transforms TransferSchedule to the format expected by TransferFormFields
 * Used for schedule updates that need to be preserved in form data
 */
export const transformScheduleToFormFields = (
  schedule: TransferSchedule,
): Partial<TransferFormFields> => {
  return {
    period: schedule.period,
    firstExecutionDate: schedule.firstExecutionDate,
    lastExecutionDate: schedule.lastExecutionDate,
    requestedExecutionAt: schedule.requestedExecutionAt,
  };
};

/**
 * Creates the review step data format from TransferReviewData
 * Used to prepare data for the TransferReviewStep component
 */
export const transformToReviewStepData = (
  reviewData: TransferReviewData,
  sourceAccount: any,
  receiptAccount: any,
  isInternalTransfer: boolean,
  isStandingOrderPayment: boolean,
) => {
  return {
    sourceAccount,
    destinationAccount:
      receiptAccount !== 'external' ? receiptAccount : undefined,
    recipientName:
      receiptAccount === 'external' ? reviewData.recipientName : undefined,
    recipientIBAN:
      receiptAccount === 'external' ? reviewData.recipientIBAN : undefined,
    amount: reviewData.amount,
    communication: reviewData.communication,
    instantTransfer: reviewData.instantTransfer,
    // Include period-related fields for scheduling information
    period: reviewData.schedule.period,
    firstExecutionDate: reviewData.schedule.firstExecutionDate,
    lastExecutionDate: reviewData.schedule.lastExecutionDate,
    requestedExecutionAt: reviewData.schedule.requestedExecutionAt,
    isInternal: isInternalTransfer,
    isStandingOrder: isStandingOrderPayment,
  };
};
